import React, { useEffect, useRef, useState } from "react";

export function FieldLabel({ htmlFor, children, required }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block font-josefin text-sm text-secondaryText mb-1.5"
    >
      {children}
      {required && <span className="text-primaryText">*</span>}
    </label>
  );
}

export function OnboardingInput({
  id,
  type = "text",
  placeholder,
  value,
  onChange,
  required,
  inputMode,
  maxLength,
}) {
  return (
    <input
      id={id}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
      inputMode={inputMode}
      maxLength={maxLength}
      className="w-full rounded-md border border-greyLight bg-white py-3 px-4 font-josefin text-secondaryText placeholder:text-textMuted outline-none focus:border-brandPurple transition duration-200 ease-in-out"
    />
  );
}

function parseOptions(children) {
  return React.Children.toArray(children)
    .filter((child) => React.isValidElement(child) && child.type === "option")
    .map((child) => ({
      value: child.props.value,
      label: child.props.children,
    }));
}

export function OnboardingSelect({ id, value, onChange, placeholder, children }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const options = parseOptions(children);
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange({ target: { id, value: optionValue } });
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`flex w-full items-center justify-between rounded-md border bg-white py-3 pl-4 pr-4 font-josefin text-sm outline-none transition duration-200 ease-in-out ${
          isOpen ? "border-brandPurple" : "border-greyLight"
        }`}
      >
        <span className={selected ? "text-secondaryText" : "text-textMuted"}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          aria-hidden
          className={`shrink-0 text-textMuted transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-labelledby={id}
          className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border border-greyLight bg-white py-1 shadow-lg"
        >
          {options.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={value === option.value}
                onClick={() => handleSelect(option.value)}
                className={`w-full px-4 py-2.5 text-left font-josefin text-sm text-secondaryText transition hover:bg-pageBg ${
                  value === option.value ? "bg-pageBg" : ""
                }`}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PrimaryButton({
  children,
  onClick,
  type = "button",
  disabled,
  variant = "green",
  className = "",
}) {
  const styles = {
    green:
      "bg-secondaryDark text-white hover:bg-primaryBack border border-transparent",
    purple:
      "bg-primaryDark text-white hover:bg-primaryText border border-transparent",
    outline:
      "bg-white text-secondaryText border border-greyLight hover:bg-pageBg",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md px-8 py-3 font-josefin text-base transition duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function InfoCallout({ children }) {
  return (
    <div className="flex gap-0 rounded-md overflow-hidden border border-primaryText/10 mb-6 sm:mb-8">
      <div className="w-1 sm:w-1.5 shrink-0 bg-primaryText" />
      <p className="flex-1 bg-backLight/60 py-4 px-4 sm:px-5 font-josefin text-sm sm:text-base text-secondaryText leading-relaxed">
        {children}
      </p>
    </div>
  );
}
